import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm } from 'react-hook-form';
import _ from 'lodash';

import { HookFormInput, HookFormToggle } from 'ui-component/HookFormComponents';
import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import {
  formConstants,
  formDefaultValues,
  formValidation,
} from 'views/org/SubOrgs/components/constants';
import { handleErr } from 'utils/functions';
import { useCreateSubOrgMutation } from 'store/slices/apiV1/org';
import { CreateSubOrgRequest } from 'types/org';
import { ALL_APPS, ALL_FEATURE_IDS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import useSnackbar from 'hooks/useSnackbar';

type AddSubOrgDialogProps = {
  dialogOpen: boolean;
  onClose: () => void;
};
const AddSubOrgDialog = ({ dialogOpen, onClose }: AddSubOrgDialogProps) => {
  const [error, setError] = useState<string | null>(null);

  const { dispatchSuccessSnackbar } = useSnackbar();

  const [createSubOrg, { isLoading: isLoadingCreateSubOrg }] =
    useCreateSubOrgMutation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FieldValues>({
    defaultValues: formDefaultValues,
    resolver: yupResolver(formValidation),
    mode: 'all',
  });
  const onSubmit = async (data: FieldValues) => {
    try {
      const payload = _.omitBy(
        data,
        (val) => _.isNil(val) || val === ''
      ) as CreateSubOrgRequest;
      await createSubOrg({ payload }).unwrap();
      dispatchSuccessSnackbar('Sub Org Created');
      handleClose();
    } catch (err) {
      handleErr(err, (errMessage: string) => setError(errMessage));
    }
  };

  const handleClose = () => {
    onClose();
    reset(formDefaultValues);
    setError(null);
  };

  return (
    <ExtendedDialog
      onCloseDialog={handleClose}
      open={dialogOpen}
      fullWidth
      maxWidth="sm"
      title="Add Sub Org"
      isForm
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isLoadingCreateSubOrg}
      submitButtonCopy="Add Sub Org"
      formSubmitError={error}
      permissionScope={{
        app: ALL_APPS.ORG_SETTINGS.id,
        accessLevel: appPermissionAccessLevels.edit,
        feature: ALL_FEATURE_IDS.SUB_ORGS,
      }}
    >
      <HookFormInput
        errors={errors}
        control={control}
        name={formConstants.name.name}
        label={formConstants.name.label}
        required
      />
      <HookFormInput
        errors={errors}
        control={control}
        name={formConstants.website.name}
        label={formConstants.website.label}
      />
      <HookFormInput
        errors={errors}
        control={control}
        name={formConstants.industry.name}
        label={formConstants.industry.label}
        required
      />
      <HookFormToggle
        errors={errors}
        control={control}
        name={formConstants.militaryUse.name}
        label={formConstants.militaryUse.label}
        helperText="We collect the industry associated with your end-use to ensure
        compliance with supplier restrictions and to help prevent delays in
        processing your orders."
      />
      <HookFormToggle
        errors={errors}
        control={control}
        name={formConstants.sandbox.name}
        label={formConstants.sandbox.label}
        helperText="Sandbox sub-orgs are used for testing and development. Orders, RFQs, Kits, and all other similar actions will not be executed for sandbox sub-orgs."
      />
    </ExtendedDialog>
  );
};

export default AddSubOrgDialog;
